/**
 * Add to Yaruta (YAR-556): the only web layer that talks to the upload edge
 * functions.
 *
 * - `createUpload` — one prepared file (lib/uploadPrep.ts) to
 *   `uploads-create`, as multipart. Every refusal comes back as an
 *   `UploadError` whose `code` the hook turns into a toast.
 * - `getUploadSignedUrl` — a short-lived URL for View Source Reference
 *   (`uploads-signed-url`).
 */
import { invokeFunction } from '@/lib/supabase';
import { parseInvokeErrorBody } from '@/lib/invoke-error';
import { toUploadKind, type UploadKind } from '@/lib/uploadKind';

export type UploadRefusal =
  | 'unsupported_type'
  | 'photo_too_large'
  | 'pdf_too_large'
  | 'pdf_too_long'
  | 'already_added'
  | 'vision_limit'
  | 'not_allowed'
  | 'failed';

/** The codes `uploads-create` answers with that the parent is told about. */
const SERVER_REFUSALS: ReadonlySet<string> = new Set<UploadRefusal>([
  'unsupported_type',
  'photo_too_large',
  'pdf_too_large',
  'pdf_too_long',
  'already_added',
  'vision_limit',
  'not_allowed',
]);

export class UploadError extends Error {
  readonly code: UploadRefusal;
  /** `already_added` only: when the earlier copy was added (ISO timestamp). */
  readonly readOn: string | null;

  constructor(code: UploadRefusal, readOn: string | null = null) {
    super(code);
    this.name = 'UploadError';
    this.code = code;
    this.readOn = readOn;
  }
}

export interface PreparedUpload {
  /** The bytes to send: the original, or the shrunk JPEG for a photo. */
  blob: Blob;
  ext: 'jpg' | 'png' | 'webp' | 'pdf';
  /** SHA-256 of the ORIGINAL file, for "already added" (Decision D5). */
  originalSha256: string;
  originalBytes: number;
  originalMime: string;
  /** The parent's filename. Not sent: the server names the object itself. */
  filename: string;
}

/**
 * Any refusal the server names becomes that code; everything else — a 401,
 * a 500, the rate limiter, a network fault, a body that is not JSON — is
 * `failed`.
 */
async function toUploadError(error: unknown): Promise<UploadError> {
  const body = await parseInvokeErrorBody(error);
  const code = body?.error;
  if (typeof code !== 'string' || !SERVER_REFUSALS.has(code)) {
    return new UploadError('failed');
  }
  const readOn =
    code === 'already_added' && typeof body?.read_on === 'string'
      ? body.read_on
      : null;
  return new UploadError(code as UploadRefusal, readOn);
}

export async function createUpload(
  householdId: string,
  prepared: PreparedUpload,
): Promise<{ emailId: string; kind: UploadKind }> {
  const form = new FormData();
  form.append('household_id', householdId);
  // A neutral name from the prepared ext: the server sniffs the bytes and
  // never stores the client's filename (B4 ruling), so the parent's own
  // filename stays on the device.
  form.append(
    'file',
    new File([prepared.blob], `upload.${prepared.ext}`, {
      type: prepared.blob.type,
    }),
  );
  form.append('original_sha256', prepared.originalSha256);
  form.append('original_bytes', String(prepared.originalBytes));
  if (prepared.originalMime) {
    form.append('original_mime', prepared.originalMime);
  }

  let result: Awaited<ReturnType<typeof invokeFunction>>;
  try {
    result = await invokeFunction('uploads-create', { body: form });
  } catch {
    throw new UploadError('failed');
  }
  if (result.error) throw await toUploadError(result.error);

  const data: unknown = result.data;
  const emailId =
    data && typeof data === 'object'
      ? (data as { email_id?: unknown }).email_id
      : undefined;
  const kind = toUploadKind(
    data && typeof data === 'object'
      ? (data as { kind?: unknown }).kind
      : undefined,
  );
  if (typeof emailId !== 'string' || !kind) throw new UploadError('failed');
  return { emailId, kind };
}

export async function getUploadSignedUrl(
  emailId: string,
): Promise<{ url: string; mimeType: string; expiresIn: number }> {
  const { data, error } = await invokeFunction('uploads-signed-url', {
    body: { email_id: emailId },
  });
  if (error) throw error;
  const answer: unknown = data;
  if (!answer || typeof answer !== 'object') {
    throw new Error('uploads-signed-url: malformed response');
  }
  const { url, mime_type, expires_in } = answer as {
    url?: unknown;
    mime_type?: unknown;
    expires_in?: unknown;
  };
  if (
    typeof url !== 'string' ||
    typeof mime_type !== 'string' ||
    typeof expires_in !== 'number'
  ) {
    throw new Error('uploads-signed-url: malformed response');
  }
  return { url, mimeType: mime_type, expiresIn: expires_in };
}
